// Ficha 04 — Forma de pago (el router). Look 1:1 con el mock `wizard/04-forma-pago.html`:
// wizard-card con head (eyebrow · "¿Cómo va a pagar?" · contexto del equipo elegido) y body con
// las `.pay-card` — SureKeep destacada (badge "Recomendada" si aplica) · Contado ·
// grid de "Otras financieras".
//
// INTEGRADA (guía §04): las opciones vienen de `GET …/financiers` (catálogo SOFOM × elegibilidad
// del producto). Selección en DOS fases como el mock (card → «Continuar»):
//   · SureKeep → POST …/payment-method (el backend crea el acreditado) → 05 Score.
//   · Contado  → POST …/payment-method → 07 Cobro.
//   · Externa  → modal → POST …/external-referral: sin extras pausa la venta y salta a la
//     Bienvenida (10); con extras la deja activa y lleva al cobro de contado de los extras.
// Card SureKeep: sin nivel previo (`session.loan.level`) muestra «Falta revisar su historial».
import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, CreditCard, ExternalLink, ShieldCheck } from 'lucide-react';
import { INTEGRATION_ACTION_LABEL, integrationFailure } from '../integrationErrors';
import { Button, ConfirmDialog, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { useFinanciers, useSetPaymentMethod, useWizardSession } from '../api/useWizard';
import { useReferralFlow, type AfterReferral } from '../api/useReferralFlow';
import {
    externalProviderName,
    isReferredWithExtras,
    paymentMethodFailure,
} from '../externalReferral';
import { SaleChangeBlockedError, type OnSaleChanged } from '../promotionSlots';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { wizardErrorMsg } from '../api/wizardError';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { WizardWarningNotice } from '../components/WizardWarningNotice';
import { previousStepSlug } from '../steps';
import { isSureKeepAvailable, sureKeepIneligibleCopy } from '../sureKeepAvailability';
import { InlineQueryError } from '../components/QueryErrorNotice';
import {
    cashChargeCents,
    financeableCartLinesOf,
    financingLineOf,
    serverSplitOf,
} from '../saleCharge';
import { cartLinesOf } from '../cartLines';
import { isAnchorUnfinanceable, saleSummaryOf } from '../buildSale';
import { ReferralExtrasNotice } from '../components/ReferralExtrasNotice';
import {
    CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE,
    isCreditInProgressOtherStore,
} from '../cashOnlyPayment';
import type { FinancierOption, WizardSession } from '../types';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';

/** `.pay-card` del mock: tarjeta seleccionable de 1.5px con ícono grande a la izquierda. */
const PAY_CARD =
    'relative flex w-full items-start gap-3.5 rounded-[10px] border-[1.5px] border-border bg-card px-4 py-3.5 text-left transition-colors';
/** `.pay-icon` — cuadro de 44px con el ícono de la opción. */
const PAY_ICON = 'flex size-11 flex-none items-center justify-center rounded-[10px]';
/** Botón que elige la opción dentro de su tarjeta; las promos van debajo, fuera del botón. */
const CARD_BUTTON = 'flex w-full items-start gap-3.5 text-left disabled:cursor-not-allowed';
/** Promos de la opción, alineadas con el texto (44px del ícono + 14px de espacio). */
const OPTION_PROMOS = 'pl-[58px]';
/** `.label-caps` — eyebrow de cada tarjeta. */
const LABEL_CAPS = 'text-[10px] font-semibold uppercase tracking-[0.06em]';

/** Las dos pistas que ofrece esta pantalla. La derivación externa no es ninguna de las dos. */
type PaymentTrack = 'SUREKEEP' | 'CASH';

/** La pista que el silo ya guardó. `null` para la derivación externa y para la venta sin decidir. */
function savedTrack(kind: WizardSession['paymentMethodKind']): PaymentTrack | null {
    if (kind === 'SUREKEEP') return 'SUREKEEP';
    if (kind === 'CASH_OR_CARD') return 'CASH';
    return null;
}

/** Lugares para las piezas de Promociones en Forma de pago (REQ-134); sin pieza no pasa nada. */
export interface PaymentMethodSlots {
    /** Se dispara tras derivar una venta con extras, antes de ir al cobro. */
    afterReferral?: AfterReferral;
    /** Se llama después de guardar la forma de pago con éxito. */
    onSaleChanged?: OnSaleChanged;
    /** Monto y promos que conserva, pierde o gana cada opción. */
    paymentOption?: (ctx: PaymentOptionContext) => ReactNode;
}

export interface PaymentOptionContext {
    track: 'SUREKEEP' | 'CASH' | 'EXTERNAL';
    session: WizardSession;
    /** Total de la venta con lo que tiene apartado ahora. */
    saleTotalCents: number;
}

export function PaymentMethodStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: PaymentMethodSlots;
}) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const {
        data: financiers = [],
        error: financiersError,
        isFetching: financiersFetching,
        refetch: retryFinanciers,
    } = useFinanciers(sessionId);
    const setMethod = useSetPaymentMethod(sessionId);
    const referral = useReferralFlow(sessionId, slots.afterReferral);
    /**
     * `undefined` = el vendedor todavía no tocó nada en esta visita → manda lo que dice la sesión.
     * Una elección suya (incluso la misma) pasa a mandar sobre lo guardado.
     */
    const [touched, setTouched] = useState<PaymentTrack | undefined>(undefined);
    const [referProvider, setReferProvider] = useState<FinancierOption | null>(null);
    // Al pasar a contado se espera a que Promociones suelte lo que solo aplica con SureKeep.
    const [releasing, setReleasing] = useState(false);
    // «Continuar» gira de principio a fin: guardar, ajustar promos y cambiar de pantalla.
    const [continuing, setContinuing] = useState(false);
    /** Lo que Promociones soltó con la nueva forma de pago: se avisa y el vendedor sigue cuando lo vio. */
    const [dropped, setDropped] = useState<{
        notices: string[];
        next: 'payment' | 'quote' | 'score';
    } | null>(null);

    if (!session) return <Loading size="lg" />;
    const anchorUnfinanceable = isAnchorUnfinanceable(session);
    // Con crédito en curso en otra sucursal solo queda Contado: ni SureKeep ni derivar.
    const cashOnly = session.cashOnly === true;
    const onlyCash = cashOnly;
    const wanted = onlyCash ? 'CASH' : (touched ?? savedTrack(session.paymentMethodKind));
    // Sin nivel SureKeep la card ni se pinta.
    const sureKeep = isSureKeepAvailable(session);
    // SureKeep viene del endpoint (INTERNAL); si el GET aún no llega, la card se pinta igual con
    // el nombre propio.
    const surekeep = financiers.find((f) => f.type === 'INTERNAL');
    const sureKeepIneligibleReason = sureKeepIneligibleCopy(surekeep);
    // SureKeep no se ofrece si no financia el equipo, aunque la sesión lo tenga guardado.
    const sureKeepBlocked =
        anchorUnfinanceable || !sureKeep || sureKeepIneligibleReason !== undefined;
    const selected = sureKeepBlocked && wanted === 'SUREKEEP' ? null : wanted;
    const product = session.product;
    const hasLines = session.reservedLines !== undefined;
    const lineCount = cartLinesOf(session).filter((l) => l.status === 'RESERVED').length;
    const financeable = financeableCartLinesOf(
        session,
        lineInfo.serviceSkus,
        lineInfo.descriptions,
    );
    const financedLineId = financingLineOf(session)?.lineId;
    const financedName = financeable.find((l) => l.lineId === financedLineId)?.name;
    const nothingFinanceable = hasLines && financeable.length === 0;
    // Con el reparto del servidor se financian el teléfono y sus extras; sin él, una sola línea.
    const hasServerSplit = serverSplitOf(session) !== undefined;
    const saleTotalCents = cashChargeCents(session);
    const cashAmount = hasLines || product ? formatMXN(saleTotalCents / 100) : '—';
    const extras = hasLines ? saleSummaryOf(session, lineInfo.descriptions).extras : [];

    const externals = financiers.filter((f) => f.type === 'EXTERNAL' && f.eligible);

    const level = session.loan?.level;
    const notEligible =
        onlyCash ||
        sureKeepIneligibleReason !== undefined ||
        nothingFinanceable ||
        anchorUnfinanceable;
    const referred = isReferredWithExtras(session);
    const referredName = externalProviderName(session.externalProvider, financiers);

    const goTo = async (
        step: 'payment' | 'build-sale' | 'product' | 'quote' | 'score',
    ): Promise<void> => {
        await navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step } });
    };

    /** Promociones revisa lo apartado con la nueva forma de pago; si no pudo, avisa con reintento y no sigue. */
    const notifyPaymentMethodChanged = async (
        sureKeep: boolean,
        retry: () => void,
    ): Promise<string[] | null> => {
        setReleasing(!sureKeep);
        try {
            const outcome = await slots.onSaleChanged?.({ kind: 'paymentMethod', sureKeep });
            return outcome ? outcome.notices : [];
        } catch (error) {
            if (!(error instanceof SaleChangeBlockedError)) return [];
            toast.error(error.message, { action: { label: 'Reintentar', onClick: retry } });
            return null;
        } finally {
            setReleasing(false);
        }
    };

    /** Con avisos se queda en pantalla para que el vendedor los vea; sin ellos sigue directo. */
    const goNext = async (
        notices: string[],
        next: 'payment' | 'quote' | 'score',
    ): Promise<void> => {
        if (notices.length > 0) {
            setDropped({ notices, next });
            return;
        }
        await goTo(next);
    };

    const continuar = async (): Promise<void> => {
        setContinuing(true);
        try {
            await advance();
        } finally {
            setContinuing(false);
        }
    };

    const advance = async (): Promise<void> => {
        setDropped(null);
        if (referred) {
            await goTo('payment');
            return;
        }
        if (!selected) return;
        try {
            if (selected === 'SUREKEEP') {
                // El backend crea el acreditado y avanza la sesión a S05.
                await setMethod.mutateAsync('SUREKEEP');
                const notices = await notifyPaymentMethodChanged(true, () => {
                    void continuar();
                });
                if (notices === null) return;
                // El score ya corrió adelantado y su pantalla de nivel ya se vio: solo se pasa por el 05
                // cuando la venta todavía no tiene nivel.
                await goNext(notices, session.loan?.level ? 'quote' : 'score');
            } else {
                await setMethod.mutateAsync('CASH_OR_CARD');
                const notices = await notifyPaymentMethodChanged(false, () => {
                    void continuar();
                });
                if (notices === null) return;
                await goNext(notices, 'payment');
            }
        } catch (e) {
            if (isCreditInProgressOtherStore(e)) {
                toast.error(CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE);
                void refresh();
                return;
            }
            const integration = integrationFailure(e);
            if (integration?.recovery === 'cash') {
                toast.error(integration.message, {
                    action: {
                        label: INTEGRATION_ACTION_LABEL.cash,
                        onClick: () => {
                            setTouched('CASH');
                        },
                    },
                });
                return;
            }
            if (integration) toast.error(integration.message);
            if (integration?.recovery === 'build-sale') {
                void refresh();
                await goTo('build-sale');
                return;
            }
            if (integration) return;
            const failure = paymentMethodFailure(e, session);
            if (failure) {
                if (failure.message !== undefined) toast.error(failure.message);
                await goTo(failure.step);
                return;
            }
            toast.error(
                wizardErrorMsg(e, 'No se pudo guardar la forma de pago — intenta de nuevo.'),
            );
        }
    };

    const confirmReferral = async (): Promise<void> => {
        if (!referProvider) return;
        if (await referral.refer(referProvider)) setReferProvider(null);
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Selección"
                title="¿Cómo va a pagar?"
                sub={
                    hasLines && lineCount > 0 ? (
                        <>
                            {lineCount === 1 ? '1 producto' : `${String(lineCount)} productos`} ·
                            Total: <span className="tabular-nums">{cashAmount}</span>
                        </>
                    ) : product ? (
                        <>
                            Equipo elegido:{' '}
                            <strong className="font-semibold text-foreground">
                                {catalogDisplayName(product, lineInfo.descriptions)}
                            </strong>{' '}
                            · <span className="tabular-nums">{cashAmount}</span>
                        </>
                    ) : (
                        <span className="text-destructive">Falta elegir el teléfono.</span>
                    )
                }
            />

            {referred ? (
                <WizardCardBody className="flex flex-col gap-4">
                    <p
                        data-testid="referred-sale-notice"
                        className="rounded-md border border-info-soft-border bg-info-soft px-3 py-2 text-[12.5px] text-info"
                    >
                        La venta pasó a {referredName}. Cobra los extras de contado.
                    </p>
                    {extras.length > 0 && <ReferralExtrasNotice extras={extras} />}
                </WizardCardBody>
            ) : (
                <WizardCardBody className="flex flex-col gap-4">
                    {dropped && (
                        <WizardWarningNotice
                            action={
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => void goTo(dropped.next)}
                                >
                                    Seguir
                                </Button>
                            }
                        >
                            {dropped.notices.map((notice, index) => (
                                <p key={index}>{notice}</p>
                            ))}
                        </WizardWarningNotice>
                    )}
                    {/* SureKeep — recomendada (INTERNAL del catálogo). Seleccionable, 2 fases. */}
                    {sureKeep && (
                        <div className="relative pt-2">
                            {/* Una opción deshabilitada nunca se marca como recomendada. */}
                            {!notEligible && (
                                <span className="absolute -top-1 left-3.5 z-10 rounded-full bg-success px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-success-foreground">
                                    Recomendada
                                </span>
                            )}
                            <div
                                className={cn(
                                    PAY_CARD,
                                    'flex-col gap-0',
                                    selected === 'SUREKEEP'
                                        ? 'border-success shadow-[0_0_0_3px_hsl(var(--success)/0.15)]'
                                        : 'hover:border-success/50',
                                    notEligible &&
                                        'cursor-not-allowed opacity-60 hover:border-border',
                                )}
                            >
                                <button
                                    type="button"
                                    disabled={notEligible || continuing}
                                    aria-pressed={selected === 'SUREKEEP'}
                                    onClick={() => {
                                        setTouched('SUREKEEP');
                                        setDropped(null);
                                    }}
                                    className={cn(CARD_BUTTON, notEligible && 'cursor-not-allowed')}
                                >
                                    <span
                                        className={cn(
                                            PAY_ICON,
                                            'bg-success text-success-foreground',
                                        )}
                                    >
                                        <ShieldCheck className="size-[22px]" aria-hidden />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className={cn(LABEL_CAPS, 'text-success')}>
                                            {surekeep?.label ?? 'Nuestra financiera'}
                                        </div>
                                        <div className="mt-0.5 text-[16px] font-semibold leading-tight">
                                            {surekeep?.name ?? 'SureKeep'}
                                        </div>
                                        <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
                                            Pago semanal desde su cuenta PagoConFiado. El equipo
                                            lleva candado.
                                        </p>
                                        <ul className="mt-2 space-y-0.5 text-[11.5px] leading-tight">
                                            <li>· Se cobra cada semana automáticamente.</li>
                                        </ul>
                                        {/* Sin nivel previo pide revisar el historial; con nivel no se anticipa nada. */}
                                        {cashOnly ? (
                                            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                {CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE}
                                            </div>
                                        ) : sureKeepIneligibleReason !== undefined ? (
                                            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                {sureKeepIneligibleReason}
                                            </div>
                                        ) : nothingFinanceable ? (
                                            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                Ningún producto de la venta se puede financiar con
                                                SureKeep. Cóbrala de contado.
                                            </div>
                                        ) : anchorUnfinanceable ? (
                                            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] leading-tight text-destructive">
                                                SureKeep no financia este equipo.
                                            </div>
                                        ) : level !== undefined ? null : (
                                            <div className="mt-3 rounded-md border border-warning-soft-border bg-warning-soft p-2 text-[11px] leading-tight text-warning">
                                                Falta revisar su historial.
                                            </div>
                                        )}
                                    </div>
                                </button>
                                {slots.paymentOption && !notEligible && (
                                    <div className={OPTION_PROMOS}>
                                        {slots.paymentOption({
                                            track: 'SUREKEEP',
                                            session,
                                            saleTotalCents,
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {selected === 'SUREKEEP' && lineCount > 1 && hasServerSplit ? (
                        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px]">
                            Se financia el teléfono y sus accesorios.
                        </p>
                    ) : selected === 'SUREKEEP' && lineCount > 1 && financedName !== undefined ? (
                        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px]">
                            Se financia {financedName}. Lo demás se cobra de contado.
                        </p>
                    ) : null}

                    {/* Contado */}
                    <div
                        className={cn(
                            PAY_CARD,
                            'flex-col gap-0',
                            selected === 'CASH'
                                ? 'border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.1)]'
                                : 'hover:border-primary/50',
                        )}
                    >
                        <button
                            type="button"
                            disabled={continuing}
                            aria-pressed={selected === 'CASH'}
                            onClick={() => {
                                setTouched('CASH');
                                setDropped(null);
                            }}
                            className={CARD_BUTTON}
                        >
                            <span className={cn(PAY_ICON, 'bg-muted text-foreground/70')}>
                                <CreditCard className="size-[22px]" aria-hidden />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className={cn(LABEL_CAPS, 'text-muted-foreground')}>
                                    Pago único
                                </div>
                                <div className="mt-0.5 text-[16px] font-semibold leading-tight">
                                    Contado
                                </div>
                                <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
                                    Efectivo, tarjeta o SPEI.
                                </p>
                                <ul className="mt-2 space-y-0.5 text-[11.5px] leading-tight">
                                    <li>· El equipo se entrega ahora mismo.</li>
                                    <li>· Sin pagos después.</li>
                                </ul>
                                {!slots.paymentOption && (
                                    <div className="mt-2 flex items-center gap-3 text-[12px]">
                                        <span
                                            className={cn(
                                                LABEL_CAPS,
                                                'tracking-[0.04em] text-muted-foreground',
                                            )}
                                        >
                                            Monto
                                        </span>
                                        <span className="text-[14px] font-semibold tabular-nums">
                                            {cashAmount}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </button>
                        {slots.paymentOption && (
                            <div className={OPTION_PROMOS}>
                                {slots.paymentOption({ track: 'CASH', session, saleTotalCents })}
                            </div>
                        )}
                    </div>

                    {/* Otras financieras (EXTERNAL elegibles del endpoint) */}
                    <div>
                        <div className={cn(LABEL_CAPS, 'mb-2 text-muted-foreground')}>
                            Otras financieras que dan crédito para este equipo
                        </div>
                        {/* Cero externas por un 500 se lee igual que un producto que solo va con
                        SureKeep, y esa frase le quita al cliente una opción que sí existe. */}
                        {financiersError ? (
                            <InlineQueryError
                                className="py-2 text-[11.5px]"
                                message={`No se pudieron cargar las otras financieras. ${wizardErrorMsg(financiersError, 'Revisa la conexión.')}`}
                                onRetry={() => void retryFinanciers()}
                                retrying={financiersFetching}
                            />
                        ) : externals.length > 0 ? (
                            <>
                                {slots.paymentOption && !onlyCash && (
                                    <div className="mb-2">
                                        {slots.paymentOption({
                                            track: 'EXTERNAL',
                                            session,
                                            saleTotalCents,
                                        })}
                                    </div>
                                )}
                                <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
                                    {externals.map((provider) => (
                                        <button
                                            key={provider.code}
                                            type="button"
                                            disabled={onlyCash}
                                            onClick={() => {
                                                setReferProvider(provider);
                                            }}
                                            className={cn(
                                                PAY_CARD,
                                                onlyCash
                                                    ? 'cursor-not-allowed opacity-60'
                                                    : 'hover:border-primary/50',
                                            )}
                                        >
                                            <span
                                                className={cn(PAY_ICON, 'bg-info-soft text-info')}
                                            >
                                                <ExternalLink className="size-5" aria-hidden />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div
                                                    className={cn(
                                                        LABEL_CAPS,
                                                        'text-muted-foreground',
                                                    )}
                                                >
                                                    {provider.label}
                                                </div>
                                                <div className="mt-0.5 text-[14px] font-semibold leading-tight">
                                                    {provider.name}
                                                </div>
                                                {onlyCash ? (
                                                    <p className="mt-1 text-[11px] leading-tight text-destructive">
                                                        {CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE}
                                                    </p>
                                                ) : (
                                                    <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
                                                        Sale de aquí y regresa para la Bienvenida.
                                                    </p>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="py-2 text-[11.5px] text-muted-foreground">
                                {cashOnly
                                    ? CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE
                                    : sureKeep
                                      ? 'Este equipo solo se ofrece con SureKeep. Si el cliente no lo quiere así, tiene que ser Contado.'
                                      : 'Ninguna otra financiera da crédito para este equipo.'}
                            </p>
                        )}
                    </div>
                </WizardCardBody>
            )}

            <WizardFootNav
                back={
                    <WizardBackLink
                        sessionId={sessionId}
                        step={previousStepSlug('payment-method', session) ?? 'build-sale'}
                    />
                }
            >
                <Button
                    size="sm"
                    disabled={!selected && !referred}
                    isLoading={continuing || setMethod.isPending || releasing}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() => void continuar()}
                >
                    Continuar
                </Button>
            </WizardFootNav>

            <ConfirmDialog
                open={referProvider !== null}
                onOpenChange={(open) => {
                    if (!open) setReferProvider(null);
                }}
                title={`Pasar la venta a ${referProvider?.name ?? ''}`}
                description={
                    extras.length > 0
                        ? 'El teléfono pasa a la financiera externa y se libera su apartado. Enseguida cobras aquí los extras.'
                        : 'La venta pasa a la financiera externa. Al regresar el cliente seguimos en Bienvenida. Esto libera el equipo reservado.'
                }
                confirmLabel="Pasar la venta"
                loading={referral.isPending}
                onConfirm={() => void confirmReferral()}
            >
                {extras.length > 0 && <ReferralExtrasNotice extras={extras} />}
            </ConfirmDialog>
        </WizardCard>
    );
}
